import React, { useState, useEffect, useCallback, useContext } from 'react';
import api from '../api/api';
import { SnackbarContext } from '../App';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle
} from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { ScrollArea } from "./ui/scroll-area";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
    Briefcase, Building, MapPin, Layers,
    RefreshCw, Pencil, Save, X, ExternalLink,
    ClipboardList
} from "lucide-react";
import { format, formatDistanceToNow } from 'date-fns';
import { getStageBadge, getVerificationIcon } from '../utils/crmHelpers';
import { useNavigate } from 'react-router-dom';

export default function ContactDetailDrawer({ isOpen, onClose, contactId, onSaveSuccess, onNavigateToList }) {
    const showSnackbar = useContext(SnackbarContext);
    const navigate = useNavigate();
    
    const [contactDetail, setContactDetail] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    
    // Editing state
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editForm, setEditForm] = useState({});
    
    // Auxiliary data mapping needs
    const [workspaceCompanies, setWorkspaceCompanies] = useState([]);

    const fetchWorkspaceCompanies = async () => {
        try {
            const res = await api.get('/api/crm/companies?limit=1000');
            setWorkspaceCompanies(res.data.data || []);
        } catch(e) {
            console.error(e);
        }
    }

    const fetchContactDetail = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await api.get(`/api/contacts/${contactId}`);
            const data = res.data;
            setContactDetail(data);
            setEditForm({
                stage: data.stage || 'LEAD',
                tags: (data.tags || []).join(', '),
                notes: data.notes || '',
                overrideEmail: data.overrideEmail || '',
                companyName: data.companyName || ''
            });
        } catch (err) {
            console.error('Failed to fetch contact detail:', err);
            showSnackbar(err.response?.data?.message || 'Failed to fetch detail', 'error');
            onClose();
        } finally {
            setIsLoading(false);
        }
    }, [contactId, showSnackbar, onClose]);

    useEffect(() => {
        if (isOpen && contactId) {
            fetchContactDetail();
            fetchWorkspaceCompanies();
        } else {
            setContactDetail(null);
            setIsEditing(false);
        }
    }, [isOpen, contactId, fetchContactDetail]);

    const handleReverify = async () => {
        if (!contactDetail) return;
        setIsSaving(true);
        try {
            const res = await api.post(`/api/contacts/${contactDetail.id}/reverify`);
            setContactDetail(res.data);
            showSnackbar('Enrichment and Re-verification started', 'success');
            if (onSaveSuccess) onSaveSuccess();
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Verification kick-off failed', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSaveContactEdit = async () => {
        setIsSaving(true);
        try {
            const updates = { ...editForm, tags: editForm.tags.split(',').map(t => t.trim()).filter(Boolean) };
            if (!updates.companyName) updates.companyId = ""; 

            const res = await api.put(`/api/contacts/${contactDetail.id}`, updates);
            setContactDetail(res.data);
            setIsEditing(false);
            showSnackbar('Contact updated successfully', 'success');
            if (onSaveSuccess) onSaveSuccess();
        } catch (err) {
            showSnackbar(err.response?.data?.message || 'Failed to update contact', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <Sheet open={isOpen} onOpenChange={onClose}>
            <SheetContent size="lg" className="p-0 h-full flex flex-col">
                <SheetHeader className="sr-only">
                    <SheetTitle>Contact Details</SheetTitle>
                </SheetHeader>
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center h-full gap-4 text-muted-foreground animate-in fade-in">
                        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                        <p className="text-sm font-medium">Loading profile...</p>
                    </div>
                ) : contactDetail ? (
                    <>
                        {/* Premium Header Profile Section */}
                        <div className="p-6 border-b border-border bg-gradient-to-br from-indigo-500/10 via-background to-background relative overflow-hidden shrink-0">
                            {/* Actions overlay */}
                            <div className="absolute top-4 right-4 flex gap-1.5 z-10">
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="h-8 shadow-sm bg-background/80 backdrop-blur-md"
                                    onClick={() => navigate(`/crm/contacts/${contactDetail.id}`)}
                                >
                                    <ExternalLink className="w-3.5 h-3.5 mr-2" />
                                    Full Page
                                </Button>
                                <Button variant="outline" size="sm" className="h-8 w-8 p-0" onClick={() => setIsEditing(!isEditing)}>
                                    <Pencil className="w-3.5 h-3.5" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={onClose}>
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>

                            <div className="flex items-start gap-4 mt-2">
                                <div className="w-16 h-16 rounded-2xl bg-indigo-500/20 border border-indigo-500/30 flex items-center justify-center text-indigo-700 font-bold text-3xl uppercase shadow-glow">
                                    {(contactDetail.firstName || contactDetail.displayName || 'U').substring(0, 1)}
                                </div>
                                <div className="space-y-1">
                                    <h2 className="text-2xl font-black tracking-tight text-foreground">{contactDetail.displayName}</h2>
                                    <p className="text-muted-foreground font-medium flex flex-wrap items-center gap-2 text-sm">
                                        <Briefcase className="w-3.5 h-3.5 opacity-70" /> {contactDetail.title || 'Unvan Yok'}
                                        {contactDetail.companyName && (
                                            <>
                                                <span className="opacity-30 mx-0.5">•</span>
                                                <Building className="w-3.5 h-3.5 opacity-70" /> {contactDetail.companyName}
                                            </>
                                        )}
                                    </p>
                                    <div className="text-xs font-semibold mt-2 flex items-center gap-1.5 text-foreground/80">
                                        {getVerificationIcon(contactDetail.verificationStatus)}
                                        {contactDetail.displayEmail}
                                        {contactDetail.overrideEmail && <Badge variant="secondary" className="text-[9px] h-4 ml-2">OVERRIDE</Badge>}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Scrolling Body */}
                        <ScrollArea className="flex-1 overflow-y-auto bg-muted/10">
                            <div className="p-6 space-y-6 pb-20">
                                
                                {/* Edit Mode Form or Normal View */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* CRM Local Section */}
                                    <div className="space-y-4 bg-card p-4 border border-border/60 rounded-xl relative shadow-sm">
                                        <div className="absolute top-0 right-0 max-w-[150px] bg-primary/10 text-[9px] text-primary font-black px-2 py-0.5 rounded-bl-xl rounded-tr-xl border-b border-l border-primary/20 tracking-wider">
                                            WORKSPACE CRM
                                        </div>
                                        
                                        <div className="space-y-1 mt-2">
                                            <span className="text-muted-foreground block text-[10px] font-black uppercase tracking-widest">Aşama (Stage)</span>
                                            {isEditing ? (
                                                <Select value={editForm.stage} onValueChange={(val) => setEditForm({ ...editForm, stage: val })}>
                                                    <SelectTrigger className="h-8 text-xs">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="LEAD">Lead</SelectItem>
                                                        <SelectItem value="CONTACTED">Contacted</SelectItem>
                                                        <SelectItem value="REPLIED">Replied</SelectItem>
                                                        <SelectItem value="MEETING_BOOKED">Meeting</SelectItem>
                                                        <SelectItem value="CLOSED_WON">Won</SelectItem>
                                                        <SelectItem value="CLOSED_LOST">Lost</SelectItem>
                                                        <SelectItem value="UNQUALIFIED">Unqualified</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                            ) : <div className="pt-1">{getStageBadge(contactDetail.stage)}</div>}
                                        </div>

                                        <div className="space-y-1">
                                            <span className="text-muted-foreground block text-[10px] font-black uppercase tracking-widest">Workspace Account</span>
                                            {isEditing ? (
                                                <div className="relative">
                                                    <Input
                                                        value={editForm.companyName}
                                                        onChange={(e) => setEditForm({ ...editForm, companyName: e.target.value })}
                                                        placeholder={contactDetail.companyName || "Company Name..."}
                                                        className="h-8 text-xs"
                                                        list="workspace-company-datalist"
                                                    />
                                                    <datalist id="workspace-company-datalist">
                                                        {workspaceCompanies.map(c => <option key={c._id} value={c.name} />)}
                                                    </datalist>
                                                </div>
                                            ) : (
                                                <span className="text-sm font-semibold truncate block" title={contactDetail.companyName || '-'}>{contactDetail.companyName || <span className="opacity-50 italic font-normal">No Company</span>}</span>
                                            )}
                                        </div>

                                        <div className="space-y-1">
                                            <span className="text-muted-foreground block text-[10px] font-black uppercase tracking-widest">Override Email</span>
                                            {isEditing ? (
                                                <Input
                                                    value={editForm.overrideEmail}
                                                    onChange={(e) => setEditForm({ ...editForm, overrideEmail: e.target.value })}
                                                    placeholder={contactDetail.canonicalEmail}
                                                    className="h-8 text-xs"
                                                />
                                            ) : (
                                                <span className="text-sm pt-1 block truncate font-medium">{contactDetail.overrideEmail || '-'}</span>
                                            )}
                                        </div>

                                        <div className="space-y-1">
                                            <span className="text-muted-foreground block text-[10px] font-black uppercase tracking-widest">Etiketler (Tags)</span>
                                            {isEditing ? (
                                                <Input
                                                    value={editForm.tags}
                                                    onChange={(e) => setEditForm({ ...editForm, tags: e.target.value })}
                                                    placeholder="tag1, tag2"
                                                    className="h-8 text-xs"
                                                />
                                            ) : (
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    {contactDetail.tags?.length > 0 ? (
                                                        contactDetail.tags.map((t, idx) => <Badge key={idx} variant="outline" className="text-[10px] border-primary/20 bg-primary/5 text-primary">{t}</Badge>)
                                                    ) : <span className="text-muted-foreground text-xs">-</span>}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Global Identity Section */}
                                    <div className="space-y-4 bg-muted/30 p-4 border border-border/50 rounded-xl relative">
                                        <div className="absolute top-0 right-0 max-w-[150px] bg-slate-500/10 text-[9px] text-slate-500 font-black px-2 py-0.5 rounded-bl-xl rounded-tr-xl border-b border-l border-slate-500/20 tracking-wider">
                                            GLOBAL CANONICAL
                                        </div>

                                        <div className="space-y-1 mt-2 hidden lg:flex flex-col items-start">
                                            <span className="text-muted-foreground block text-[10px] font-black uppercase tracking-widest">E-mail (Gerçek)</span>
                                            <div className="flex items-center gap-2 group">
                                                <span className="font-medium text-sm truncate max-w-[150px] inline-block">{contactDetail.canonicalEmail || '-'}</span>
                                                <Button size="icon" variant="ghost" className="h-6 w-6 opacity-40 group-hover:opacity-100 hover:bg-emerald-500/10 hover:text-emerald-600" onClick={handleReverify} disabled={isSaving} title="Yeniden Doğrula">
                                                    <RefreshCw className={`h-3 w-3 ${isSaving ? 'animate-spin' : ''}`} />
                                                </Button>
                                            </div>
                                            {contactDetail.verificationStatus !== 'UNKNOWN' && (
                                                <div className="text-[9px] text-muted-foreground/70 font-semibold leading-tight">
                                                    Sağlayıcı: {contactDetail.verificationSource || 'API'} <br/> {contactDetail.lastVerifiedAt ? formatDistanceToNow(new Date(contactDetail.lastVerifiedAt)) : 'Bilinmiyor'} önce
                                                </div>
                                            )}
                                        </div>

                                        <div className="space-y-1 mt-2 flex flex-col items-start lg:hidden">
                                            <span className="text-muted-foreground block text-[10px] font-black uppercase tracking-widest">E-mail (Gerçek)</span>
                                            <div className="flex items-center gap-2 group">
                                                <span className="font-medium text-sm">{contactDetail.canonicalEmail || '-'}</span>
                                            </div>
                                        </div>

                                        <div className="space-y-1">
                                            <span className="text-muted-foreground block text-[10px] font-black uppercase tracking-widest">Lokasyon</span>
                                            <span className="flex items-center gap-1.5 text-sm font-medium"><MapPin className="w-3.5 h-3.5 text-muted-foreground" /> {contactDetail.location || '-'}</span>
                                        </div>

                                        <div className="space-y-1">
                                            <span className="text-muted-foreground block text-[10px] font-black uppercase tracking-widest">LinkedIn & Enrichment</span>
                                            <div className="flex flex-col gap-2 mt-1">
                                                <span>{contactDetail.linkedinUrl ? <a href={contactDetail.linkedinUrl} target="_blank" rel="noreferrer" className="text-blue-500 hover:underline text-xs font-semibold flex items-center gap-1"><ExternalLink className="w-3 h-3"/> Profili Gör</a> : <span className="text-xs">-</span>}</span>
                                                <div className="flex gap-2">
                                                    {contactDetail.isStale && <Badge variant="destructive" className="text-[9px] h-4">STALE (BAYAT)</Badge>}
                                                    {!contactDetail.isStale && contactDetail.enrichmentSource && <Badge variant="outline" className="text-emerald-600 border-emerald-300 text-[9px] h-4 bg-emerald-50/50">{contactDetail.enrichmentSource} (GÜNCEL)</Badge>}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Custom Notes block outside */}
                                <div className="space-y-2">
                                    <span className="text-muted-foreground block text-[10px] font-black uppercase tracking-widest">CRM Notları</span>
                                    {isEditing ? (
                                        <Textarea
                                            value={editForm.notes}
                                            onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                                            placeholder="Yalnızca sizin workspace'inizde görünür..."
                                            className="min-h-[80px] text-sm bg-background"
                                        />
                                    ) : (
                                        <div className="bg-background border border-border/80 p-3 rounded-xl text-sm text-foreground min-h-[60px] whitespace-pre-wrap shadow-sm">
                                            {contactDetail.notes || <span className="italic text-muted-foreground/60 text-xs">Not bulunmuyor.</span>}
                                        </div>
                                    )}
                                </div>

                                {/* Edit Actions Context */}
                                {isEditing && (
                                    <div className="flex justify-end gap-3 pt-2">
                                        <Button variant="outline" size="sm" onClick={() => setIsEditing(false)} disabled={isSaving} className="h-9 rounded-xl">
                                            İptal
                                        </Button>
                                        <Button size="sm" onClick={handleSaveContactEdit} disabled={isSaving} className="h-9 rounded-xl bg-primary text-primary-foreground shadow-lg shadow-primary/20">
                                            <Save className="w-3.5 h-3.5 mr-1.5" /> Değişiklikleri Kaydet
                                        </Button>
                                    </div>
                                )}

                                {/* Lists / Engagement */}
                                {!isEditing && (
                                    <>
                                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                                            <div className="bg-card p-3 rounded-xl border border-border shadow-sm flex flex-col">
                                                <span className="text-muted-foreground text-[9px] font-black uppercase tracking-widest mb-1">Total Opens</span>
                                                <span className="text-2xl font-black text-amber-600">{contactDetail.openCount || 0}</span>
                                            </div>
                                            <div className="bg-card p-3 rounded-xl border border-border shadow-sm flex flex-col">
                                                <span className="text-muted-foreground text-[9px] font-black uppercase tracking-widest mb-1">Total Clicks</span>
                                                <span className="text-2xl font-black text-purple-600">{contactDetail.clickCount || 0}</span>
                                            </div>
                                            <div className="bg-card p-3 rounded-xl border border-border shadow-sm flex flex-col col-span-2">
                                                <span className="text-muted-foreground text-[9px] font-black uppercase tracking-widest mb-1">Crm Lists</span>
                                                <div className="flex flex-wrap gap-1 mt-1">
                                                    {contactDetail.lists?.length > 0 ? (
                                                        contactDetail.lists.map(list => (
                                                            <div key={list._id} className="text-[10px] font-semibold border border-primary/20 bg-primary/5 text-primary rounded-md px-2 py-0.5 hover:bg-primary/10 cursor-pointer transition-colors" onClick={() => onNavigateToList ? onNavigateToList(list._id) : window.location.href = `/crm/lists/${list._id}`}>
                                                                <ClipboardList className="w-3 h-3 mr-1 mb-0.5 inline" /> {list.name}
                                                            </div>
                                                        ))
                                                    ) : <span className="text-muted-foreground text-xs italic opacity-60">Not in any list</span>}
                                                </div>
                                            </div>
                                        </div>

                                        {/* Sequence Visibility */}
                                        {contactDetail.sequences && (
                                            <div className="bg-card border border-border/70 shadow-sm rounded-xl p-5 relative overflow-hidden">
                                                <div className="absolute top-0 left-0 w-1 h-full bg-primary/40"></div>
                                                <h4 className="text-xs font-black uppercase tracking-widest flex items-center gap-2 mb-4 text-foreground/80">
                                                    <Layers className="w-4 h-4 text-primary" />
                                                    Sequence Enrollments
                                                </h4>
                                                
                                                {contactDetail.sequences.length === 0 ? (
                                                    <p className="text-xs text-muted-foreground italic">This contact is not currently enrolled in any sequences.</p>
                                                ) : (
                                                    <div className="space-y-4">
                                                        {contactDetail.sequences.map((seqAssign, idx) => (
                                                            <div key={idx} className="flex flex-col gap-2 border border-border/50 bg-muted/20 p-3 rounded-lg">
                                                                <div className="flex justify-between items-start">
                                                                    <div className="flex flex-col">
                                                                        <span className="text-sm font-bold">{seqAssign.sequenceName}</span>
                                                                        <span className="text-[10px] font-semibold text-muted-foreground mt-0.5">Step {seqAssign.currentStepNumber} of {seqAssign.totalSteps}</span>
                                                                    </div>
                                                                    <Badge variant="outline" className={`text-[9px] h-4.5 font-bold ${seqAssign.assignmentStatus === 'ACTIVE' ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30' :
                                                                        seqAssign.assignmentStatus === 'BOUNCED' ? 'bg-destructive/10 text-destructive border-destructive/30' :
                                                                            seqAssign.assignmentStatus === 'REPLIED' ? 'bg-primary/10 text-primary border-primary/30' :
                                                                                seqAssign.assignmentStatus === 'FAILED' ? 'bg-amber-500/10 text-amber-600 border-amber-500/30' :
                                                                                    'bg-accent text-accent-foreground border-border'
                                                                        }`}>{seqAssign.assignmentStatus}</Badge>
                                                                </div>
                                                                <div className="text-[10px] font-medium text-muted-foreground flex items-center gap-2 mt-1">
                                                                    <div className="w-1.5 h-1.5 rounded-full bg-primary/40"></div>
                                                                    {['ACTIVE', 'PENDING', 'DEFERRED'].includes(seqAssign.assignmentStatus)
                                                                        ? `Next execution: ${seqAssign.nextStepScheduledAt ? formatDistanceToNow(new Date(seqAssign.nextStepScheduledAt), { addSuffix: true }) : 'Immediate'}`
                                                                        : `Finished/Stopped`
                                                                    }
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                        
                                        {/* Metadata Footer */}
                                        <div className="flex flex-col xs:flex-row xs:items-center justify-between text-[9px] text-muted-foreground/60 font-semibold pt-6 tracking-wider uppercase">
                                            <span>Added: {contactDetail.createdAt ? format(new Date(contactDetail.createdAt), 'MMM dd, yyyy HH:mm') : 'Unknown'}</span>
                                            <span>Last Contacted: {contactDetail.lastContactedAt ? format(new Date(contactDetail.lastContactedAt), 'MMM dd, yyyy') : 'Never'}</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        </ScrollArea>
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center h-full gap-4 text-muted-foreground">
                        <p className="text-sm font-medium">Contact could not be found.</p>
                        <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
